import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { ScrollText, Undo2 } from "lucide-react";

const ACTION_COLORS = {
  created: "bg-emerald-100 text-emerald-800",
  updated: "bg-sky-100 text-sky-800",
  status_changed: "bg-sky-100 text-sky-800",
  reverted: "bg-amber-100 text-amber-800",
  activated: "bg-emerald-100 text-emerald-800",
  deactivated: "bg-slate-200 text-slate-700",
  assigned: "bg-violet-100 text-violet-800",
  employee_assigned: "bg-violet-100 text-violet-800",
  marked_inactive: "bg-rose-100 text-rose-800",
  employee_inactivated: "bg-rose-100 text-rose-800",
  calculation_created: "bg-amber-100 text-amber-800",
  calculation_updated: "bg-amber-100 text-amber-800",
  calculation_deleted: "bg-rose-100 text-rose-800",
  note: "bg-slate-100 text-slate-700",
};

export default function LogViewerDialog({ open, onClose, title, endpoint, revertEndpointBuilder }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState("");

  const fetchLogs = () => {
    if (!endpoint) return;
    setLoading(true);
    api.get(endpoint)
      .then(({ data }) => setLogs(data || []))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endpoint]);

  const doRevert = async (lg) => {
    if (!revertEndpointBuilder || !lg?.id) return;
    setReverting(lg.id);
    try {
      await api.post(revertEndpointBuilder(lg));
      toast.success("Reverted to previous status");
      fetchLogs();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setReverting("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText size={16} className="text-violet-600" />
            {title || "Activity Logs"}
            <Badge variant="outline" className="ml-2 text-[11px]">{logs.length} {logs.length === 1 ? "entry" : "entries"}</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-[13px]">Loading logs…</div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-[13px]" data-testid="log-empty">No activity recorded yet.</div>
        ) : (
          <div className="relative pl-4" data-testid="log-list">
            <div className="absolute left-1 top-1 bottom-1 w-px bg-slate-200" />
            <div className="space-y-3">
              {logs.map((lg, i) => {
                const canRevert = revertEndpointBuilder && lg.revertable && !lg.reverted_at;
                return (
                  <div key={lg.id || i} className="relative" data-testid={`log-row-${i}`}>
                    <div className="absolute -left-3.5 top-1.5 h-2 w-2 rounded-full bg-violet-500 ring-2 ring-white" />
                    <div className="flex items-start gap-2 flex-wrap">
                      <Badge className={`${ACTION_COLORS[lg.action] || "bg-slate-100 text-slate-700"} text-[10px] uppercase tracking-wider`}>
                        {(lg.action || "note").replace(/_/g, " ")}
                      </Badge>
                      <span className="text-[10px] text-slate-400 mono">
                        {(lg.at || "").slice(0, 16).replace("T", " ")}
                      </span>
                      {lg.emp_name && <span className="text-[10px] text-slate-500">· {lg.emp_name}</span>}
                      {lg.reverted_at && (
                        <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]" data-testid={`log-reverted-${i}`}>
                          Reverted {lg.reverted_at.slice(0, 16).replace("T", " ")}
                        </Badge>
                      )}
                      {canRevert && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="ml-auto h-6 px-2 text-[10px] text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`log-revert-${i}`} disabled={reverting === lg.id}>
                              <Undo2 size={10} className="mr-1" /> Revert
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Revert this change?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will restore <span className="font-semibold">{lg.employee_name || "employee"}</span>&apos;s attendance for {lg.date} back to <span className="font-semibold">{lg.old_status || "(no status)"}</span>. A new audit log will be created.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => doRevert(lg)} className="bg-amber-600 hover:bg-amber-700" data-testid={`log-revert-confirm-${i}`}>Yes, Revert</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                    <div className="text-[12.5px] text-slate-700 mt-0.5">{lg.note}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">by {lg.by_name || "—"}{lg.reverted_by_name ? ` · reverted by ${lg.reverted_by_name}` : ""}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
